import { useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import type { Client, Professional } from "../../types";
// import { EditAppointmentModal } from "./components/EditAppointmentModal";
import { parseBrazilDate } from "../utils/date";
import { WeeklySchedule } from "./components/WeeklySchedule";
import { AppointmentEditDrawer } from "./components/AppointmentEditDrawer";
import { AppointmentCreateDrawer } from "./components/AppointmentCreateDrawer";
import { EvolutionModal } from "./components/EvolutionModal";
import { AppointmentsHeader } from "./components/AppointmentsHeader";
import { AppointmentsFiltersDialog } from "./components/AppointmentsFiltersDialog";
import { DeleteAppointmentDialog } from "./components/DeleteAppointmentDialog";
import { AppointmentCard } from "./components/AppointmentCard";
import { navigate } from "../../lib/router";
import { Combobox } from "../../components/ui/combobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../components/ui/dialog";
import { Filter, X, ChevronLeft, ChevronRight, Check } from "../../components/icons";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { apptLabels } from "./labels";
import { cn } from "../../lib/utils";

export function AppointmentsPage() {
  const { lang } = useLanguage();
  const t = apptLabels[lang];
  // Floating time: no timezone usage
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [employeeFilter, setEmployeeFilter] = useState<number | "">("");
  const [showDone, setShowDone] = useState(true);
  // const [editAppt, setEditAppt] = useState<any | null>(null);
  const [requireEvolution, setRequireEvolution] = useState(true);
  const [hasActivePlan, setHasActivePlan] = useState<boolean | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [deleteAppt, setDeleteAppt] = useState<any | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [fromApp, setFromApp] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [editingApptId, setEditingApptId] = useState<number | string | null>(null);
  const [evolutionApptId, setEvolutionApptId] = useState<number | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [showSaturday, setShowSaturday] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("agenda_showSaturday");
      return saved === "true"; // Default is false if not found
    }
    return false;
  });
  const [showSunday, setShowSunday] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("agenda_showSunday");
      return saved === "true"; 
    }
    return false;
  });
  const [showCompanyBlocks, setShowCompanyBlocks] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("agenda_showCompanyBlocks");
      return saved !== "false"; // Default is true
    }
    return true;
  });
  const [showProfessionalBlocks, setShowProfessionalBlocks] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("agenda_showProfessionalBlocks");
      return saved !== "false"; // Default is true
    }
    return true;
  });

  useEffect(() => {
    localStorage.setItem("agenda_showSaturday", String(showSaturday));
  }, [showSaturday]);

  useEffect(() => {
    localStorage.setItem("agenda_showSunday", String(showSunday));
  }, [showSunday]);

  useEffect(() => {
    localStorage.setItem("agenda_showCompanyBlocks", String(showCompanyBlocks));
  }, [showCompanyBlocks]);

  useEffect(() => {
    localStorage.setItem("agenda_showProfessionalBlocks", String(showProfessionalBlocks));
  }, [showProfessionalBlocks]);


  // Selected date (Brazil timezone base)
  // Selected week (Sunday of the week)
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    return d;
  });
  const professionalOptions = useMemo(
    () => [
      { value: "all", label: t.common.all },
      ...professionals.map((e) => ({ value: String(e.id), label: e.name })),
    ],
    [professionals, t.common.all]
  );

  const handlePrevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };

  const handleNextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };

  const handleGoToToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    setWeekStart(d);
  };

  const mysqlDate = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  };

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    d.setMilliseconds(d.getMilliseconds() - 1);
    return d;
  }, [weekStart]);


  const load = async () => {
    const [profs, cls, exc] = await Promise.all([
        api.listProfessionals(), 
        api.listClients(),
        api.listScheduleExceptions()
    ]);
    if (profs.ok) setProfessionals(profs.data);
    if (cls.ok) setClients(cls.data);
    if (exc.ok) setExceptions(exc.data);
    const params: any = { start: mysqlDate(weekStart), end: mysqlDate(weekEnd) };
    if (employeeFilter) {
      params.employee_id = Number(employeeFilter);
    }
    const appts = await api.listAppointments(params);
    const paymentsRes = await api.listPayments({ status: 'pending' });
    
    let overdueClientIds = new Set<number>();
    if (paymentsRes.ok) {
      const now = new Date();
      overdueClientIds = new Set(
        paymentsRes.data
          .filter(p => {
            const dueDateStr = (p as any).due_date || p.paid_at;
            if (!dueDateStr) return false;
            return new Date(dueDateStr) < now;
          })
          .map(p => p.client_id)
      );
    }

    if (appts.ok) {
      const enriched = appts.data.map((a: any) => ({
        ...a,
        has_late_payment: a.client_id ? overdueClientIds.has(a.client_id) : false
      }));
      setItems(enriched);
    }
    setLoading(false);
  };

  const markDone = async (appt: any) => {
    const res = await api.updateAppointment(appt.id, { done: true });
    if (!res.ok) {
      toast.error(res.error || t.toasts.updateError);
    } else {
      toast.success(t.toasts.markedDone);
      load();
    }
  };

  const unmarkDone = async (appt: any) => {
    const res = await api.updateAppointment(appt.id, { done: false });
    if (!res.ok) {
      toast.error(res.error || t.toasts.updateError);
    } else {
      toast.success(t.toasts.unmarked);
      load();
    }
  };

  const requestDelete = (appt: any) => {
    setDeleteAppt(appt);
    setConfirmDelete(true);
  };

  useEffect(() => { load(); }, [weekStart.getTime(), employeeFilter, fromApp, currentUserId]);

  useEffect(() => {
    (async () => {
      // Detecta origem "app" para alternar o layout
      try {
        const src = localStorage.getItem("source");
        setFromApp(src === "app");
      } catch {}
      const comp = await api.getCompany();
      if (comp.ok) {
        setRequireEvolution(comp.data.require_evolution !== false);
        const active =
          !!comp.data.plan &&
          !!comp.data.next_renewal &&
          new Date(comp.data.next_renewal) > new Date();
        setHasActivePlan(active);
      }
      const me = await api.me();
      if (me.ok) {
        setCurrentUserId(me.data.id);
        // Se vier do app e ainda não há filtro manual, inicia filtrando pelo usuário logado
        if (localStorage.getItem("source") === "app" && !employeeFilter) {
          setEmployeeFilter(me.data.id);
        }
      }
    })();
  }, []);

  useEffect(() => {
    const close = () => { setNewMenuOpen(false); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNewMenuOpen(false);
      }
    };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const visibleItems = useMemo(() => showDone ? items : items.filter((a: any) => !a?.done), [items, showDone]);
  const visibleItemsSorted = useMemo(() => {
    const arr = [...visibleItems];
    arr.sort((a: any, b: any) => String(a.start_datetime).localeCompare(String(b.start_datetime)));
    return arr;
  }, [visibleItems]);
  // Base sem filtro de "Mostrar finalizadas" para seções baseadas no tempo atual
  const baseSorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a: any, b: any) => String(a.start_datetime).localeCompare(String(b.start_datetime)));
    return arr;
  }, [items]);

  // Seções: "Acontecendo agora" e "Próximas consultas" no modo app
  const makeNowBr = () => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return parseBrazilDate(`${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`);
  };
  const [nowBr, setNowBr] = useState<Date>(makeNowBr());
  useEffect(() => {
    setNowBr(makeNowBr());
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNowBr(makeNowBr()), 30_000);
    return () => clearInterval(id);
  }, []);

  const splitNowAndUpcoming = useMemo(() => {
    const toEnd = (it: any) => {
      // Prefere end_datetime quando houver; caso contrário usa start + duração
      if (it.end_datetime) return parseBrazilDate(it.end_datetime);
      const start = parseBrazilDate(it.start_datetime);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + Number(it.duration_minutes || 60));
      return end;
    };
    // Use a lista completa para permitir múltiplas simultâneas, independentemente de "Mostrar finalizadas"
    let filtered = baseSorted;
    if (employeeFilter) {
      filtered = filtered.filter((a: any) => a.employee_id === Number(employeeFilter));
    }
    const happening: any[] = [];
    const upcoming: any[] = [];
    const past: any[] = [];
    const nowTs = +nowBr;
    const GRACE_MS = 2 * 60 * 1000; // 2 minutos de tolerância para o fim
    for (const it of filtered) {
      const start = parseBrazilDate(it.start_datetime);
      const end = toEnd(it);
      const st = +start;
      const en = +end;
      // Classificação estritamente por timestamps BR
      if (st <= nowTs && nowTs < (en + GRACE_MS)) {
        happening.push(it);
      } else if (st > nowTs) {
        upcoming.push(it);
      } else {
        past.push(it);
      }
      try {
        // Depuração opcional para casos fora do lugar
        if (typeof window !== 'undefined' && (it.__debug_once !== true)) {
          (it as any).__debug_once = true;
          // eslint-disable-next-line no-console
          console.log('[NowSplit]', {
            id: it.id,
            start: it.start_datetime,
            end: it.end_datetime,
            start_ts: st,
            end_ts: en,
            now_ts: nowTs,
            bucket: st <= nowTs && nowTs < en ? 'happening' : (st > nowTs ? 'upcoming' : 'past')
          });
        }
      } catch {}
    }
    return { happening, upcoming, past };
  }, [baseSorted, fromApp, currentUserId, nowBr]);

  return (
    <>
      <AppointmentsHeader
        weekStart={weekStart}
        handlePrevWeek={handlePrevWeek}
        handleNextWeek={handleNextWeek}
        handleGoToToday={handleGoToToday}
        setIsCreateOpen={setIsCreateOpen}
        setFiltersOpen={setFiltersOpen}
        employeeFilter={employeeFilter}
        setEmployeeFilter={setEmployeeFilter}
        professionalOptions={professionalOptions}
        showDone={showDone}
        setShowDone={setShowDone}
        showSaturday={showSaturday}
        setShowSaturday={setShowSaturday}
        showSunday={showSunday}
        setShowSunday={setShowSunday}
        showCompanyBlocks={showCompanyBlocks}
        setShowCompanyBlocks={setShowCompanyBlocks}
        showProfessionalBlocks={showProfessionalBlocks}
        setShowProfessionalBlocks={setShowProfessionalBlocks}
        fromApp={fromApp}
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className={fromApp ? "lg:col-span-12" : "lg:col-span-12 xl:col-span-12"}>
          <div className="h-[calc(100vh-220px)]">
            <WeeklySchedule
              startDate={weekStart}
              items={visibleItems}
              exceptions={exceptions}
              professionals={professionals}
              onEdit={(a) => setEditingApptId(a.id)}
              onMarkDone={markDone}
              onUnmarkDone={unmarkDone}
              onDelete={requestDelete}
              onFillEvolution={(a) => setEvolutionApptId(a.id)}
              showSaturday={showSaturday}
              showSunday={showSunday}
              showCompanyBlocks={showCompanyBlocks}
              showProfessionalBlocks={showProfessionalBlocks}
            />
          </div>
        </section>
      </div>

      <EvolutionModal
        appointmentId={evolutionApptId}
        isOpen={!!evolutionApptId}
        onClose={() => setEvolutionApptId(null)}
        onSuccess={() => {
          setEvolutionApptId(null);
          load();
        }}
      />

      <AppointmentEditDrawer
        apptId={editingApptId}
        onClose={() => setEditingApptId(null)}
        onSuccess={() => {
          setEditingApptId(null);
          load();
        }}
      />

      <AppointmentCreateDrawer
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={() => {
          setIsCreateOpen(false);
          load();
        }}
      />

      <AppointmentsFiltersDialog
        isOpen={filtersOpen}
        onOpenChange={setFiltersOpen}
        employeeFilter={employeeFilter}
        setEmployeeFilter={setEmployeeFilter}
        showDone={showDone}
        setShowDone={setShowDone}
        showPast={showPast}
        setShowPast={setShowPast}
        showSaturday={showSaturday}
        setShowSaturday={setShowSaturday}
        showSunday={showSunday}
        setShowSunday={setShowSunday}
        showCompanyBlocks={showCompanyBlocks}
        setShowCompanyBlocks={setShowCompanyBlocks}
        showProfessionalBlocks={showProfessionalBlocks}
        setShowProfessionalBlocks={setShowProfessionalBlocks}
        professionalOptions={professionalOptions}
      />

      <DeleteAppointmentDialog
        isOpen={confirmDelete}
        onOpenChange={setConfirmDelete}
        onConfirm={async () => {
          if (deleteAppt) {
            const res = await api.deleteAppointment(deleteAppt.id);
            if (!res.ok) {
              toast.error(res.error || t.toasts.deleteError);
            } else {
              toast.success(t.toasts.deleted);
              load();
            }
          }
          setDeleteAppt(null);
        }}
      />
      {/* EditAppointmentModal removido: edição acontece em tela dedicada */}
    </>
  );
}
